import moment from 'moment-timezone';

import {
  type DateRange,
  EventTrigger,
  type Media,
  type MediaRequest,
  MediaRequestStatus,
  MediaType,
  Severity,
  type Trip,
  type UUID,
  type Vehicle,
  type VehicleGroup,
} from '@cobliteam/common-types';

import { useVehicle, useVehicleGroup } from '@cobliteam/hooks';

import type { MediaRequestData } from '../types/MediaRequest';

export const DEFAULT_EVENT_TRIGGER = [
  EventTrigger.HARSH_ACCELERATION,
  EventTrigger.HARSH_CORNERING,
  EventTrigger.DISTRACTED_DRIVING,
  EventTrigger.TAILGATING,
  EventTrigger.HARSH_BREAKING,
];
export const DEFAULT_MEDIA_REQUEST_STATUS = [MediaRequestStatus.FINISHED, MediaRequestStatus.PENDING];

export enum MediaTableType {
  EVENT_MEDIA = 'EVENT_MEDIA',
  FOOTAGE_MEDIA = 'FOOTAGE_MEDIA',
}

export enum EventTableType {
  EVENT_MEDIA = 'EVENT_MEDIA',
  FAVORITE_EVENT_MEDIA = 'FAVORITE_EVENT_MEDIA',
}

export enum RequestTableType {
  REQUEST_MEDIA = 'REQUEST_MEDIA',
  FAVORITE_REQUEST_MEDIA = 'FAVORITE_REQUEST_MEDIA',
}

export enum TripImageHistoryTableType {
  ALL_TRIPS = 'ALL_TRIPS',
  PENDING_NOMINATE_TRIPS = 'PENDING_NOMINATE_TRIPS',
}

export const getVehiclesFromSelectedVehicleGroups = (vehicleGroups: VehicleGroup[], vehicles: Vehicle[], wantedVehicleGroups: UUID[]): Vehicle[] => {
  const vehiclesFromSelectedGroups: Vehicle[] = [];

  wantedVehicleGroups.forEach((vehicleGroupId) => {
    const vehicleGroup = vehicleGroups.find(({ id }) => id === vehicleGroupId);
    const vehiclesFromGroup = vehicleGroup?.vehicleIds || [];

    vehiclesFromGroup.forEach((vehicleId) => {
      const vehicle = vehicles.find(({ id }) => id === vehicleId);

      if (vehicle && !vehiclesFromSelectedGroups.includes(vehicle)) {
        vehiclesFromSelectedGroups.push(vehicle);
      }
    });
  });

  return vehiclesFromSelectedGroups;
};
export const getSeverityTagLabel = (severity: Severity, count?: number) => {
  switch (severity) {
    case Severity.VERY_SEVERE:
      return count !== undefined ? `${count} Gravíssima` : 'Gravíssima';
    case Severity.SEVERE:
      return count !== undefined ? `${count} Grave` : 'Grave';
    case Severity.MEDIUM:
      return count !== undefined ? `${count} Média` : 'Média';
    default:
      return count !== undefined ? `${count} Gravíssima` : 'Gravíssima';
  }
};

export enum Months {
  Janeiro = 0,
  Fevereiro = 1,
  Março = 2,
  Abril = 3,
  Maio = 4,
  Junho = 5,
  Julho = 6,
  Agosto = 7,
  Setembro = 8,
  Outubro = 9,
  Novembro = 10,
  Dezembro = 11,
}

export const DEFAULT_MEDIA_REQUEST_DATA: MediaRequestData = {
  hasNotificationEnabled: false,
};

export const MEDIA_DEFAULT_DURATION_IN_MILLIS = 60000;

export const DEFAULT_MEDIA_REQUEST_FOOTAGE_AVAILABILITY = [true, false];

export const MEDIA_REQUEST_MODAL_BASE_PERIOD = {
  before: moment().subtract(1, 'years').startOf('day').toDate(),
  after: moment().endOf('day').toDate(),
};

export const MEDIA_REQUEST_MODAL_TRIPS_LIMIT = {
  min: 0,
  max: 10000,
};

export const MEDIA_REQUEST_MODAL_MEDIAS_LIMIT = {
  min: 0,
  max: 10000,
};

export const formatTimeHourAndMinute = (timestamp: number) => moment(timestamp).format('HH:mm');

export const getRequestPeriod = (mediaRequestData?: MediaRequestData, selectedTrip?: Trip): DateRange | undefined => {
  const { mediaType, requestMinute, requestDate } = mediaRequestData as MediaRequestData;

  if (!selectedTrip) {
    return undefined;
  }

  if (mediaType === MediaType.TIMELAPSE) {
    return {
      start: selectedTrip.startTimestamp,
      end: selectedTrip.endTimestamp,
    };
  }

  const startPeriodString = formatTimeHourAndMinute(selectedTrip.startTimestamp);
  const endPeriodString = formatTimeHourAndMinute(selectedTrip.endTimestamp);

  if (requestMinute === startPeriodString) {
    return {
      start: selectedTrip.startTimestamp,
      end: selectedTrip.startTimestamp + MEDIA_DEFAULT_DURATION_IN_MILLIS,
    };
  }

  if (requestMinute === endPeriodString) {
    return {
      start: selectedTrip.endTimestamp - MEDIA_DEFAULT_DURATION_IN_MILLIS,
      end: selectedTrip.endTimestamp,
    };
  }

  const [hours, minutes] = requestMinute!.split(':');
  const hourAndMinuteStartTimestamp = moment(requestDate)
    .set({
      hour: Number.parseInt(hours, 10),
      minute: Number.parseInt(minutes, 10),
      second: 0,
      millisecond: 0,
    })
    .valueOf();

  return {
    start: hourAndMinuteStartTimestamp,
    end: hourAndMinuteStartTimestamp + MEDIA_DEFAULT_DURATION_IN_MILLIS,
  };
};

export const getPeriodErrorMessage = (mediaRequestData?: MediaRequestData, medias?: Media[], trip?: Trip): string | undefined => {
  if (!medias || !mediaRequestData || !mediaRequestData.requestPeriodStart || !mediaRequestData.requestDate || !mediaRequestData.mediaType) {
    return undefined;
  }

  if (mediaRequestData?.mediaType === MediaType.TIMELAPSE) {
    const media = medias.find((m) => m.timestamp === mediaRequestData.requestPeriodStart && m.mediaType === mediaRequestData.mediaType);

    if (media) {
      const mediaDetails = media.mediaDetails as MediaRequest;

      switch (mediaDetails.status) {
        case MediaRequestStatus.FINISHED:
          return 'Já existe um vídeo disponível deste período.';
        case MediaRequestStatus.PENDING:
          return 'Existe um vídeo solicitado pendente para esse período.';
        default:
          return undefined;
      }
    }
  } else if (mediaRequestData?.mediaType === MediaType.FOOTAGE_VIDEO && mediaRequestData.requestMinute && trip) {
    const selectedPeriod = getRequestPeriod(mediaRequestData, trip);
    const selectedPeriodMoment = moment(selectedPeriod?.start);
    const selectedPeriodTimestamp = selectedPeriodMoment.valueOf();

    if (!selectedPeriodMoment.isBetween(trip.startTimestamp, trip.endTimestamp, 'minute', '[]')) {
      return 'Insira um horário dentro do período.';
    }

    const media = medias.find((m) => m.timestamp === selectedPeriodTimestamp && m.mediaType === mediaRequestData.mediaType);

    if (media) {
      const mediaDetails = media.mediaDetails as MediaRequest;

      switch (mediaDetails.status) {
        case MediaRequestStatus.FINISHED:
          return 'Já existe um vídeo disponível deste minuto.';
        case MediaRequestStatus.PENDING:
          return 'Existe um vídeo solicitado pendente para esse minuto.';
        default:
          return undefined;
      }
    }
  }

  return undefined;
};

export const useGetVehicles = () => {
  const { getVehicles } = useVehicle();
  const { data: vehicles = [] } = getVehicles({
    enabled: true,
    staleTime: 300000,
  });

  return vehicles;
};

export const useGetVehicleGroups = () => {
  const { getVehicleGroups } = useVehicleGroup();
  const { data: vehicleGroups = [] } = getVehicleGroups({
    staleTime: 300000,
  });

  return vehicleGroups;
};

export const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noreferrer');
};
